import { createFileRoute, Link } from "@tanstack/react-router";
import portraitAsset from "@/assets/a-propos.webp.asset.json";
const portrait = portraitAsset.url;

export const Route = createFileRoute("/a-propos")({
  head: () => ({
    meta: [
      { title: "Caroline Balech — Naturopathe à Toulouse | Rénergescence" },
      { name: "description", content: "Découvrez le parcours et la philosophie de Caroline Balech, naturopathe et praticienne en soins énergétiques à Toulouse." },
      { property: "og:title", content: "Caroline Balech — Rénergescence" },
      { property: "og:description", content: "Naturopathe et praticienne en soins énergétiques à Toulouse." },
      { property: "og:url", content: "/a-propos" },
      { property: "og:image", content: portrait },
    ],
    links: [{ rel: "canonical", href: "https://renergescence.com/a-propos" }],
  }),
  component: APropos,
});

function APropos() {
  return (
    <>
      <section className="pt-20 pb-12 md:pt-28">
        <div className="mx-auto max-w-[1100px] px-6 text-center">
          <p className="eyebrow">Praticienne en santé naturelle · Énergéticienne · Masseuse bien-être</p>
          <h1 className="mt-5 font-display text-5xl font-light italic md:text-7xl">
            À propos de Caroline Balech — Naturopathe &amp; énergéticienne à Toulouse
          </h1>
        </div>
        <div className="mx-auto mt-14 max-w-[800px] px-6">
          <div className="overflow-hidden rounded-[2rem]">
            <img src={portrait} alt="Caroline Balech" width={1024} height={1024} className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[720px] px-6">
          <h2 className="font-display text-3xl font-light md:text-4xl">Message personnel</h2>
          <blockquote
            className="mt-8 border-l-2 pl-6 font-display text-xl italic leading-relaxed md:text-2xl"
            style={{ borderColor: "#B59C7A", color: "#2D2B28" }}
          >
            <p>« C'est un véritable privilège pour moi de pouvoir vous accompagner au travers de diverses techniques, d'être à l'écoute de vos maux physiques et émotionnels et d'être présente à vos côtés sur votre chemin du mieux-être.</p>
            <p className="mt-4">Aussi, il me tient à cœur de vous remercier pour la confiance que vous m'accordez et pour ce que vous me permettez de vivre au quotidien.</p>
            <p className="mt-4">C'est une voie de Partage, de Bienveillance et d'Évolution que l'on s'autorise à emprunter ensemble. »</p>
            <footer className="mt-6 text-sm not-italic tracking-[0.18em] uppercase" style={{ color: "#6a6258" }}>— Caroline</footer>
          </blockquote>
        </div>
      </section>

      <section style={{ backgroundColor: "var(--color-surface)" }} className="py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="eyebrow">Ma philosophie</p>
          <blockquote className="mt-6 font-display text-3xl font-light italic leading-snug md:text-5xl">
            « Le cabinet Rénergescence est l'aboutissement de plusieurs années d'expériences, de rencontres, de partages et d'échanges. Il est né d'un désir de transmettre un message du cœur au travers des mains. »
          </blockquote>
        </div>
      </section>

      <section className="py-20 md:py-28" style={{ backgroundColor: "#FBF8F3" }}>
        <div className="mx-auto max-w-[920px] px-6">
          <div className="text-center">
            <p className="eyebrow">Itinéraire</p>
            <h2 className="mt-3 font-display text-4xl font-light italic md:text-5xl" style={{ color: "#2D2B28" }}>Mon parcours</h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed" style={{ color: "var(--color-muted-foreground)" }}>
              Des études d'esthétique à la pratique en santé naturelle, un chemin construit étape par étape, au service du mieux-être.
            </p>
          </div>

          <ol className="mt-16 divide-y" style={{ borderColor: "#e8e0d8", ['--tw-divide-opacity' as never]: 1 }}>
            {[
              { 
                y: "Études", 
                t: "BTS Esthétique Cosmétique & Parfumerie · CQP Spa Praticien", 
                d: "Intégration de l'École Supérieure Privée Marge Verlair. Stages en spas et thalassothérapies étoilés. Spécialisation dans l'utilisation des appareils hydrothérapeutiques et maîtrise de diverses techniques de massages bien-être." 
              },
              { 
                y: "Expérimentation", 
                t: "Approche holistique", 
                d: "Découverte de l'aromathérapie, de la médecine traditionnelle chinoise et initiation aux bains de flottaison aux côtés d'une professionnelle passionnée et formée à la naturopathie. Désir d'approfondir mes connaissances et ma prise en charge." 
              },
              { 
                y: "Révélation", 
                t: "Magnétisme & radiesthésie", 
                d: "Prise de conscience de mon magnétisme naturel grâce à des événements personnels et professionnels successifs. Stage auprès du GNOMA SNAMAP — transmissions et apprentissage en magnétisme et radiesthésie (niveaux 1 et 2)." 
              },
              { 
                y: "Formation", 
                t: "Praticienne en Santé Naturelle — E.I.B.E. Toulouse", 
                d: "Formation approfondie à l'E.I.B.E. Toulouse : anatomie, physiologie, neuroscience, biologie, micro-nutrition, phytothérapie, aromathérapie, auriculothérapie, médecine traditionnelle chinoise. Une vision globale et pluridimensionnelle de l'humain." 
              },
              { 
                y: "2020", 
                t: "Création du cabinet Rénergescence", 
                d: "Ouverture du cabinet à Toulouse — une nouvelle étape sur le chemin du mieux-être, à vos côtés." 
              },
            ].map((f) => (
              <li
                key={f.t}
                className="grid gap-4 py-10 md:grid-cols-[200px_1fr] md:gap-12 md:py-12"
                style={{ borderColor: "#e8e0d8" }}
              >
                <div className="font-display text-xl italic md:text-2xl font-light" style={{ color: "var(--color-sage-text)" }}>
                  {f.y}
                </div>
                <div>
                  <h3 className="font-display text-2xl font-light leading-snug md:text-3xl" style={{ color: "#2D2B28" }}>
                    {f.t}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#4A4742" }}>
                    {f.d}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <p className="mx-auto mt-14 max-w-2xl text-center text-sm italic" style={{ color: "var(--color-muted-foreground)" }}>
            Exercice dans le respect strict de la déontologie naturopathique. <Link to="/deontologie" className="underline">En savoir plus</Link>.
          </p>
        </div>
      </section>

      <section style={{ backgroundColor: "#F0EBE4" }} className="py-20 md:py-24 text-center">
        <div className="mx-auto max-w-2xl px-6">
          <h2 className="font-display text-3xl font-light italic md:text-5xl" style={{ color: "#2D2B28" }}>Réserver une séance</h2>
          <Link
            to="/contact"
            className="mt-8 inline-block rounded-[6px] px-8 py-4 text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#7A8A76" }}
          >
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </>
  );
}
